import { Snackbar } from 'components/Snackbar';
import { IUseLoading, useLoader } from 'hooks/utilHooks';
import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { PartnerService } from 'services';
import { IDiscountsHistoric, IPartner, IPartnerList } from 'types/Partner';

import { AlertColor } from '@mui/material';

export interface IPartnersAdminContext {
  listPartners: Function;
  page: number;
  setPage: Function;
  maxPage: number;
  loading: boolean;
  partners: IPartnerList;
  uf: string;
  setUf: Function;
  query: string;
  setQuery: Function;
  getPartnersCb: Function;
  getPartner: Function;
  deleteItem: Function;
  onSubmit: Function;
  onSubmitReject: Function;
  getPartnerDiscount: Function;
  loadingDelete: any;
  partner: IPartner | undefined;
  discountsHistoric: IDiscountsHistoric | undefined;
  success: string;
  error: string;
  isRejected: boolean;
  setIsRejected: Function;
  loadReject: IUseLoading;
  loadAccept: IUseLoading;
}

interface ISnackbar {
  message: string | null;
  variant?: AlertColor | undefined;
}

export const PartnersAdminContext = createContext<IPartnersAdminContext>(
  {} as IPartnersAdminContext
);

type Props = {
  children: ReactNode;
};

export const PartnersAdminProvider = ({ children }: Props) => {
  const [partners, setPartners] = useState<IPartnerList>({
    count: 0,
    list: [],
    totalPages: 1,
    uf: '',
  });

  const [discountsHistoric, setDiscountsHistoric] =
    useState<IDiscountsHistoric>();

  const [snackbar, setSnackbar] = useState<ISnackbar>({
    message: '',
    variant: 'info',
  });

  const [uf, setUf] = useState('');
  const [page, setPage] = useState(1);
  const [maxPage, setMaxPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const loadingDelete = useLoader();
  const [query, setQuery] = useState('');
  const [inPage, setInPage] = useState(page);

  const [partner, setPartner] = useState<IPartner>();

  const loadReject = useLoader();
  const loadAccept = useLoader();
  const [success, setSuccess] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isRejected, setIsRejected] = useState<boolean>(false);

  const getPartners = useCallback(
    async (page: number, filter: string, uf: string) => {
      const partners = await PartnerService.getPartnersList(
        page,
        10,
        filter,
        uf
      );
      if (partners.status === 200) {
        return partners.data;
      } else {
        return [];
      }
    },
    []
  );

  const getPartnersCb = useCallback(
    async (force = false) => {
      if (inPage !== page || force === true) {
        const partners = await getPartners(page, query, uf);
        setPartners(partners);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setPartners, inPage, query, page, getPartners, uf]
  );

  const getPartner = useCallback(async (id: string) => {
    if (!id) {
      return;
    }

    try {
      setPartner({} as IPartner);
      setDiscountsHistoric({} as IDiscountsHistoric);
      setIsRejected(false);
      setLoading(true);
      setSuccess('');
      setError('');
      const partnerView = await PartnerService.findById({ id });
      setPartner(partnerView);
    } catch (reason) {
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteItem = async (id: string) => {
    if (!id) {
      return;
    }

    try {
      loadingDelete.showLoader();
      await PartnerService.deletePartner(id);

      getPartnersCb(true);

      setSnackbar({
        message: 'Parceiro apagado com sucesso',
        variant: 'success',
      });
    } catch (error) {
      setSnackbar({
        message: 'Não foi possível apagar o parceiro',
        variant: 'error',
      });
    } finally {
      loadingDelete.hideLoader();
    }
  };

  const onSubmit = async (id: string) => {
    try {
      loadAccept.showLoader();
      setIsRejected(false);
      await PartnerService.acceptPartner(id);
      setSuccess('Parceiro aprovado com sucesso.');
    } catch (error) {
      setError('Não foi possível aprovar o parceiro.');
    } finally {
      loadAccept.hideLoader();

      setTimeout(() => {
        setError('');
        setSuccess('');
      }, 5000);
    }
  };

  const onSubmitReject = async (value: { message: string }, id: string) => {
    if (isRejected) {
      if (Boolean(value) || value.message !== '') {
        try {
          loadReject.showLoader();
          await PartnerService.rejectPartner(id, value);
          setSuccess('Mensagem de reprovação enviada.');
        } catch (error) {
          setError('Não foi possível enviar mensagem de recusa.');
        } finally {
          loadReject.hideLoader();

          setTimeout(() => {
            setError('');
            setSuccess('');
          }, 5000);
        }
      }
    }
  };

  const getPartnerDiscount = useCallback(
    async (sort: string, idPartner: string) => {
      if (!idPartner) {
        return;
      }

      try {
        const { data } = await PartnerService.getPartnerDiscount(
          sort,
          idPartner
        );
        setDiscountsHistoric(data);

        return data;
      } catch (error) {
        setSnackbar({
          message: 'Não foi possível listar os descontos',
          variant: 'error',
        });
        return [];
      }
    },
    []
  );

  useEffect(() => {
    getPartners(1, '', '').then((partners) => {
      setLoading(false);
      setPartners(partners);
      setMaxPage(partners.totalPages);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setPartners, setLoading]);

  useEffect(() => {
    setInPage(page);
  }, [page, setInPage, getPartnersCb]);

  useEffect(() => {
    getPartnersCb();
  }, [setInPage, getPartnersCb]);

  const listPartners = () => {
    getPartners(1, '', '').then((partners) => {
      setLoading(false);
      setPartners(partners);
      setMaxPage(partners.totalPages);
    });
  };

  return (
    <PartnersAdminContext.Provider
      value={{
        listPartners,
        page,
        setPage,
        maxPage,
        loading,
        partners,
        uf,
        setUf,
        query,
        setQuery,
        getPartnersCb,
        deleteItem,
        loadingDelete,
        getPartner,
        partner,
        onSubmit,
        onSubmitReject,
        success,
        error,
        isRejected,
        setIsRejected,
        loadAccept,
        loadReject,
        getPartnerDiscount,
        discountsHistoric,
      }}
    >
      {children}

      <Snackbar
        text={snackbar.message}
        snackbarOpen={Boolean(snackbar.message)}
        setSnackbarOpen={() => {
          setSnackbar({
            message: '',
          });
        }}
        severityType={snackbar.variant}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      />
    </PartnersAdminContext.Provider>
  );
};

